// --- BroadcastChannel for cross-tab logout sync ---
const authChannel = new BroadcastChannel('auth');
authChannel.onmessage = (event) => {
  if (event.data === 'logout') {
    sessionStorage.clear();
    // Use the same logic as main logout: redirect to SSO signoff if possible
    try {
      const envConfig = (globalThis as any).envConfig;
      const idToken = sessionStorage.getItem('idToken');
      if (envConfig?.endSessionUrl) {
        const endSessionUrl = `${envConfig.endSessionUrl}?id_token_hint=${encodeURIComponent(idToken || '')}`;
        globalThis.location.replace(endSessionUrl);
      } else {
        globalThis.location.replace(`${globalThis.location.origin}/login`);
      }
    } catch {
      globalThis.location.replace(`${globalThis.location.origin}/login`);
    }
  }
};
// --- End BroadcastChannel setup ---

export const authService = {
  // Get OAuth config from window
  getOAuthConfig: () => {
    const envConfig = (globalThis as any).envConfig;
    if (!envConfig) {
      throw new Error("Environment config not loaded. Check window.envConfig");
    }

    return {
      clientId: envConfig.clientId,
      redirectUri: `${globalThis.location.origin}/auth/callback`,
      scope: "openid profile email",
      authUrl: envConfig.authUrl,
      tokenUrl: envConfig.tokenUrl,
      revokeUrl: envConfig.revokeUrl,
      userInfoUrl: envConfig.userInfoUrl,
      jwksUrl: envConfig.jwksUrl,
      endSessionUrl: envConfig.endSessionUrl,
    };
  },

  // Step 1: Redirect user to PingFed login with PKCE
  initiateLogin: () => {
    console.log("Initiating OAuth login with PKCE...");

    const OAUTH_CONFIG = authService.getOAuthConfig();

    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier();
    generateCodeChallenge(codeVerifier).then((codeChallenge) => {
      const state = generateRandomState();

      // Store verifier and state in sessionStorage for callback
      sessionStorage.setItem("oauth_code_verifier", codeVerifier);
      sessionStorage.setItem("oauth_state", state);

      const params = new URLSearchParams({
        response_type: "code",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        client_id: OAUTH_CONFIG.clientId,
        redirect_uri: OAUTH_CONFIG.redirectUri,
        scope: OAUTH_CONFIG.scope,
        state: state,
      });

      const loginUrl = `${OAUTH_CONFIG.authUrl}?${params.toString()}`;
      console.log("Authorization URL:", loginUrl);
      console.log("Code Verifier stored:", codeVerifier);

      // Use replace to prevent back-button navigation to current page
      globalThis.location.replace(loginUrl);
    });
  },

  // Step 2: Exchange authorization code for tokens (with client_secret)
  exchangeCodeForToken: async (code: string) => {
    try {
      console.log("Exchanging authorization code for tokens...");

      const OAUTH_CONFIG = authService.getOAuthConfig();

      const codeVerifier = sessionStorage.getItem("oauth_code_verifier");
      if (!codeVerifier) {
        throw new Error("Code verifier not found in sessionStorage");
      }

      console.log("Code Verifier:", codeVerifier);
      console.log("Authorization Code:", code);
      console.log("Client ID:", OAUTH_CONFIG.clientId);
      console.log("Redirect URI:", OAUTH_CONFIG.redirectUri);

      // Use URLSearchParams as per Kaiser example
      const params = new URLSearchParams();
      params.append("grant_type", "authorization_code");
      params.append("code", code);
      params.append("code_verifier", codeVerifier);
      params.append("redirect_uri", OAUTH_CONFIG.redirectUri);
      params.append("client_id", OAUTH_CONFIG.clientId);

      const response = await fetch(OAUTH_CONFIG.tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      const data = await response.json();
      console.log("Token Response Status:", response.status);
      console.log("Token Response:", data);

      if (!response.ok) {
        console.error("Token exchange failed:", data);
        throw new Error(
          `Token exchange failed: ${data.error} - ${data.error_description}`,
        );
      }

      console.log("Token exchange successful");

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        idToken: data.id_token,
        expiresIn: data.expires_in,
        userId: data.sub,
      };
    } catch (error) {
      console.error("Token exchange error:", error);
      throw error;
    }
  },

  // Step 3: Refresh access token using refresh token
  refreshAccessToken: async (): Promise<boolean> => {
    try {
      console.log("Attempting to refresh access token...");

      const OAUTH_CONFIG = authService.getOAuthConfig();

      const refreshToken = sessionStorage.getItem("refreshToken");
      if (!refreshToken) {
        console.log("No refresh token available");
        return false;
      }

      const params = new URLSearchParams();
      params.append("grant_type", "refresh_token");
      params.append("refresh_token", refreshToken);
      params.append("client_id", OAUTH_CONFIG.clientId);

      console.log("Refresh token request sent");

      const response = await fetch(OAUTH_CONFIG.tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      const data = await response.json();
      console.log("Refresh response status:", response.status);

      if (!response.ok) {
        console.error("Token refresh failed:", data);
        // Clear tokens if refresh fails
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("idToken");
        return false;
      }

      // Update access token
      sessionStorage.setItem("accessToken", data.access_token);
      if (data.refresh_token) {
        sessionStorage.setItem("refreshToken", data.refresh_token);
      }
      if (data.id_token) {
        sessionStorage.setItem("idToken", data.id_token);
      }

      console.log("Access token refreshed successfully");
      return true;
    } catch (error) {
      console.error("Token refresh error:", error);
      return false;
    }
  },

  // Check authentication status and attempt refresh if needed
  checkAndRefreshAuth: async (): Promise<boolean> => {
    const accessToken = sessionStorage.getItem("accessToken");
    const refreshToken = sessionStorage.getItem("refreshToken");

    console.log("Checking authentication status...");
    console.log("AccessToken exists:", !!accessToken);
    console.log("RefreshToken exists:", !!refreshToken);

    // Scenario 1: No tokens at all - need to login
    if (!accessToken && !refreshToken) {
      console.log("Scenario 1: No tokens - user needs to login");
      return false;
    }

    // Scenario 2: Access token exists and valid - user is authenticated
    if (accessToken && !isTokenExpired(accessToken)) {
      console.log("Scenario 2: AccessToken valid - user authenticated");
      return true;
    }

    // Scenario 3: Access token expired but refresh token exists - try to refresh
    if ((!accessToken || isTokenExpired(accessToken)) && refreshToken) {
      console.log("Scenario 3: AccessToken expired - attempting refresh");
      const refreshed = await authService.refreshAccessToken();
      if (refreshed) {
        return true;
      }
    }

    // Scenario 4 & 5: Refresh failed or no refresh token - need to login again
    console.log(
      "Scenario 4/5: Refresh failed or unavailable - user needs to login",
    );
    return false;
  },

  getAccessToken: (): string | null => {
    return sessionStorage.getItem("accessToken");
  },

  getRefreshToken: (): string | null => {
    return sessionStorage.getItem("refreshToken");
  },

  getIdToken: (): string | null => {
    return sessionStorage.getItem("idToken");
  },

  isAuthenticated: (): boolean => {
    const token = sessionStorage.getItem("accessToken");
    return !!token && !isTokenExpired(token);
  },

  logout: async () => {
    console.log("Logout started: Revoking tokens and ending session…");

    try {
      const config = authService.getOAuthConfig();
      const accessToken = sessionStorage.getItem("accessToken");
      const refreshToken = sessionStorage.getItem("refreshToken");
      const idToken = sessionStorage.getItem("idToken");

      // Step 1: Revoke the access token at PingFed
      if (accessToken) {
        try {
          console.log("Revoking access token at PingFed...");

          const revokeParams = new URLSearchParams();
          revokeParams.append("token", accessToken);
          revokeParams.append("client_id", config.clientId);

          const revokeResponse = await fetch(config.revokeUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: revokeParams.toString(),
          });

          console.log(
            "Access token revoke response status:",
            revokeResponse.status,
          );
        } catch (error) {
          console.error("Access token revocation failed:", error);
          // Continue anyway - still need to revoke refresh token
        }
      }

      // Step 2: Revoke the refresh token at PingFed
      if (refreshToken) {
        try {
          console.log("Revoking refresh token at PingFed...");

          const revokeRefreshParams = new URLSearchParams();
          revokeRefreshParams.append("token", refreshToken);
          revokeRefreshParams.append("client_id", config.clientId);
          revokeRefreshParams.append("token_type_hint", "refresh_token");

          const refreshRevokeResponse = await fetch(config.revokeUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: revokeRefreshParams.toString(),
          });

          console.log(
            "Refresh token revoke response status:",
            refreshRevokeResponse.status,
          );
        } catch (error) {
          console.error("Refresh token revocation failed:", error);
          // Continue anyway - still need to clear storage and end session
        }
      }

      // Step 3: Clear all local and session storage BEFORE manipulating history
      console.log("Clearing local and session storage...");
      sessionStorage.clear();
      sessionStorage.clear();
      console.log("Storage cleared");

      // Step 4: Overwrite browser history to prevent back-button navigation to cached OAuth pages
      // This replaces history entries so user cannot navigate back to authenticated/OAuth pages
      console.log("Overwriting browser history to prevent back navigation...");

      const loginUrl = `${globalThis.location.origin}/login`;

      // Replace current history entry with login page
      globalThis.history.replaceState(null, "", loginUrl);

      // Push multiple login entries to "bury" the OAuth history
      // This ensures back button lands on login, not cached OAuth authorize URLs
      for (let i = 0; i < 3; i++) {
        globalThis.history.pushState(null, "", loginUrl);
      }

      console.log("Browser history overwritten with login entries");

      // Step 5: End the OAuth session at PingFed using SLO endpoint
      console.log("Ending OAuth session at PingFed SLO endpoint...");

      const endSessionUrl = `${config.endSessionUrl}?id_token_hint=${encodeURIComponent(idToken || "")}`;

      console.log("Redirecting to PingFed logout endpoint");

      // Use location.replace to prevent adding to history (back won't return here)
      globalThis.location.replace(endSessionUrl);
    } catch (error) {
      console.error("Logout error:", error);

      // Force cleanup even if error occurs
      sessionStorage.clear();
      sessionStorage.clear();

      // Overwrite history even on error
      const loginUrl = `${globalThis.location.origin}/login`;
      globalThis.history.replaceState(null, "", loginUrl);
      for (let i = 0; i < 3; i++) {
        globalThis.history.pushState(null, "", loginUrl);
      }

      // Redirect to login using replace
      globalThis.location.replace(loginUrl);
    } 
    finally {
      // At the end of logout, broadcast to all tabs
      try {
        authChannel.postMessage('logout');
      } catch (e) {
        console.log(e);
      }
    }
  },
};

// Generate PKCE code verifier (43 characters minimum)
function generateCodeVerifier(): string {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const length = 43;

  // Use cryptographically secure random values
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(randomValues[i] % characters.length);
  }

  console.log("Generated code verifier (43 chars):", result);
  return result;
}

// Generate PKCE code challenge from verifier (SHA256)
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await crypto.subtle.digest("SHA-256", data);

  // Convert to base64url
  const bytes = new Uint8Array(hash);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCodePoint(bytes[i]);
  }

  const codeChallenge = btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');

  console.log("Generated code challenge:", codeChallenge);
  return codeChallenge;
}

// Helper: Generate random state for CSRF protection
function generateRandomState(): string {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);

  // Convert to hex string
  const state = Array.from(randomBytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  sessionStorage.setItem("oauth_state", state);
  console.log("Generated state:", state);
  return state;
}

// Helper: Check if token is expired
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload?.exp) return true;

    const expirationTime = payload.exp * 1000;
    return Date.now() >= expirationTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
}
